import { Badge, Popover, PopoverTrigger, Spinner, Text, useStyledSystem } from "@hopper-ui/components";
import { type BulkOperationSummary } from "../../api/types.ts";
import { InitializingCategoryKeys, MigrateCategoryKeys, isTenantScanning } from "../../api/crawlCategories.ts";
import { client } from "../../api/client.ts";
import { initialCrawlOverviewQueryOptions } from "../../api/routes/initial-crawl-overview.ts";
import { MixpanelProperty, useInstallWorkspaceHeader, useTrackButtonClicked } from "@sg-protect/shell";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { Button } from "react-aria-components";
import { ACTIVITY_POPOVER_OFFSET, ActivityCoachmark, ActivityStartedAnnouncement } from "./ActivityCoachmark.tsx";
import { ActivityPanel } from "./ActivityPanel.tsx";
import styles from "./ActivityPill.module.css";
import { useActionStartedFeedback } from "./useActionStartedFeedback.ts";
import type { FailedOperation, RunningOperation } from "../remediation/BulkProvider.tsx";
import { getTenantName } from "../../utils/entityHelpers.ts";
import { WarningIcon } from "@hopper-ui/icons";

const PILL_STYLES = {
    display: "flex",
    alignItems: "center",
    gap: "inline-sm",
    backgroundColor: "primary-weak",
    backgroundColorHover: "primary-weak-hover",
    backgroundColorActive: "primary-weak-press",
    color: "primary",
    border: "none",
    borderRadius: "pill",
    paddingLeft: "inset-sm",
    paddingRight: "inset-xs",
    paddingTop: "inset-xs",
    paddingBottom: "inset-xs",
    cursor: "pointer"
} as const;

const FAILURE_PILL_STYLES = {
    ...PILL_STYLES,
    backgroundColor: "danger",
    backgroundColorHover: "danger-weak",
    backgroundColorActive: "danger-weak-hover",
    color: "danger"
} as const;

export interface ActivityPillProps {
    runningOperations: RunningOperation[];
    failedOperations: FailedOperation[];
    summaries: BulkOperationSummary[];
    coachmarkDurationMs?: number;
}

export function ActivityPill({
    runningOperations,
    failedOperations,
    summaries,
    coachmarkDurationMs
}: ActivityPillProps) {
    const isWorkspaceScoped = useInstallWorkspaceHeader(client);
    const trackButtonClicked = useTrackButtonClicked();

    const { data: scanData, isError } = useQuery({ ...initialCrawlOverviewQueryOptions(), enabled: isWorkspaceScoped });
    const data = isError ? undefined : scanData;

    const hasFailure = failedOperations.length > 0;
    const { stylingProps } = useStyledSystem(hasFailure ? FAILURE_PILL_STYLES : PILL_STYLES);

    const migrateAssessments = data?.migrateAssessments ?? [];
    const isPrimaryScanning = isTenantScanning(data?.primary, InitializingCategoryKeys);
    const scanningMigrateAssessments = migrateAssessments.filter(tenant =>
        isTenantScanning(tenant, MigrateCategoryKeys)
    );

    const scanningCount = (isPrimaryScanning ? 1 : 0) + scanningMigrateAssessments.length;

    const operationCards = runningOperations.map(operation => ({
        operation,
        summary: summaries.find(summary => summary.bulkId === operation.bulkId),
        tenantName: data?.primary?.displayName ?? undefined
    }));

    const activityCount = scanningCount + runningOperations.length + failedOperations.length;

    const triggerRef = useRef<HTMLButtonElement>(null);
    const activityBulkIds = useMemo(
        () => [...runningOperations, ...failedOperations].map(operation => operation.bulkId),
        [runningOperations, failedOperations]
    );
    const { isCoachmarkOpen, setIsCoachmarkHovered, closeCoachmark, startCount } = useActionStartedFeedback(
        triggerRef,
        styles.pulse,
        activityCount,
        activityBulkIds,
        coachmarkDurationMs
    );

    const handlePress = () => {
        closeCoachmark();
        trackButtonClicked("Activity Pill", "Activity Center", {
            [MixpanelProperty.NumberOfRunningOperations]: runningOperations.length,
            [MixpanelProperty.NumberOfFailedOperations]: failedOperations.length,
            [MixpanelProperty.NumberOfScanningTenants]: scanningCount
        });
    };

    return (
        <>
            <ActivityStartedAnnouncement startCount={startCount} />
            {activityCount > 0 && (
                <>
                    <PopoverTrigger>
                        <Button {...stylingProps} ref={triggerRef} onPress={handlePress}>
                            <ActivityPillIcon hasFailure={hasFailure} />
                            <Text size="sm" color={hasFailure ? "danger" : "primary"} data-public>
                                Activity
                            </Text>
                            <Badge variant="secondary" backgroundColor={hasFailure ? "danger-strong" : undefined}>
                                {activityCount}
                            </Badge>
                        </Button>
                        <Popover
                            aria-label="Activity"
                            isAutoWidth
                            placement="bottom end"
                            offset={ACTIVITY_POPOVER_OFFSET}
                            // On a short viewport the popover shrinks; the panel's body must absorb that so only it scrolls.
                            containerProps={{ display: "flex", flexDirection: "column" }}
                        >
                            <ActivityPanel
                                primaryTenantName={data?.primary && getTenantName(data.primary)}
                                primaryTenant={isPrimaryScanning ? data?.primary : undefined}
                                migrateTenants={scanningMigrateAssessments}
                                operationCards={operationCards}
                                failedOperations={failedOperations}
                            />
                        </Popover>
                    </PopoverTrigger>
                    <ActivityCoachmark
                        triggerRef={triggerRef}
                        isOpen={isCoachmarkOpen}
                        onHoverChange={setIsCoachmarkHovered}
                    />
                </>
            )}
        </>
    );
}

interface ActivityPillIconProps {
    hasFailure: boolean;
}

function ActivityPillIcon({ hasFailure }: ActivityPillIconProps) {
    return hasFailure ? (
        <WarningIcon size="sm" fill="danger" aria-label="Activity failed" />
    ) : (
        <Spinner size="sm" aria-label="Activity in progress" />
    );
}
